import React, { forwardRef } from 'react';
import { tr } from '../../i18n/i18n';
import { useTranslation } from 'react-i18next';
import { AppButton } from '../atoms';
import dayjs from 'dayjs';
import { PrimaryModal, ModalHandle } from '../Modals';

interface SessionConfirmationModalProps {
  openingBalance: number | null;
  notes: string;
  onConfirm: () => void;
  onCancel: () => void;
  loading?: boolean;
}

/**
 * Session Confirmation Modal - Phase 3
 *
 * Purpose: Final confirmation before starting payment session
 * Shows summary of session details for user review
 *
 * Flow: SessionBlockingModal → StartSessionModal → SessionConfirmationModal → API Call
 *
 * Epic: CVA-2286
 *
 * Pattern: Uses PrimaryModal with forwardRef (matches ConfirmationModal standard)
 */
const SessionConfirmationModal = forwardRef<ModalHandle, SessionConfirmationModalProps>(
  ({ openingBalance, notes, onConfirm, onCancel, loading = false }, ref) => {
    const { t } = useTranslation();
    // Get organization currency from localStorage
    const orgCurrency = localStorage.getItem('currency') || 'LKR';

    // Format currency value
    const formatCurrency = (amount: number | null) => {
      if (amount === null) return `${orgCurrency} 0.00`;
      return `${orgCurrency} ${amount.toFixed(2)}`;
    };

    // Close handler: Call onCancel callback (PrimaryModal handles actual closing)
    const handleClose = () => {
      onCancel();
    };

    return (
      <PrimaryModal
        ref={ref}
        width="500px"
        showHeader={false}
        onClose={loading ? undefined : handleClose}
        content={
          <div className="flex flex-col gap-6">
            {/* Header */}
            <div className="border-b pb-4">
              <h2 className="text-xl font-semibold text-gray-900">
                {t('payment_session.confirm_start', 'Confirm Session Start')}
              </h2>
            </div>
            {/* Success Icon */}
            <div className="flex justify-center">
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center">
                <i className="pi pi-check text-green-600 text-3xl" />
              </div>
            </div>

            {/* Confirmation Message */}
            <div className="text-center space-y-2">
              <h3 className="text-lg font-semibold text-gray-900">
                {t('payment_session.ready_to_start', 'Ready to Start Session?')}
              </h3>
              <p className="text-sm text-gray-600">
                {t(
                  'payment_session.review_details',
                  'Please review the details below before confirming',
                )}
              </p>
            </div>

            {/* Session Details Summary */}
            <div className="bg-gray-50 rounded-lg p-4 space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-gray-700">
                  {t('payment_session.opening_balance', 'Opening Balance:')}
                </span>
                <span className="text-base font-semibold text-gray-900">
                  {formatCurrency(openingBalance)}
                </span>
              </div>

              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-gray-700">
                  {t('payment_session.start_time', 'Start Time:')}
                </span>
                <span className="text-sm text-gray-900">{dayjs().format('hh:mm A')}</span>
              </div>

              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-gray-700">
                  {t('common.date_colon', 'Date:')}
                </span>
                <span className="text-sm text-gray-900">{dayjs().format('MMM D, YYYY')}</span>
              </div>

              {notes && (
                <div className="pt-2 border-t border-gray-200">
                  <span className="text-sm font-medium text-gray-700 block mb-1">
                    {t('common.notes_colon', 'Notes:')}
                  </span>
                  <p className="text-sm text-gray-900 whitespace-pre-wrap">{notes}</p>
                </div>
              )}
            </div>

            {/* Warning Message */}
            <div className="flex items-start gap-3 p-3 rounded-lg bg-yellow-50 border border-yellow-200">
              <i className="pi pi-exclamation-triangle text-yellow-600 text-base mt-0.5" />
              <p className="text-xs text-yellow-900">
                {tr(
                  'payment_session.close_at_shift_end_notice',
                  "Once started, you'll need to close this session at the end of your shift with an actual cash count.",
                )}
              </p>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3">
              <AppButton
                label={t('common.button_cancel')}
                className="flex-1 border-gray-300 text-gray-700 hover:bg-gray-50"
                onClick={handleClose}
                disabled={loading}
              />
              <AppButton
                label={loading ? 'Starting...' : tr('g.confirm_start', 'Confirm & Start')}
                className="flex-1 bg-purple-600 text-white hover:bg-purple-700"
                onClick={onConfirm}
                disabled={loading}
                loading={loading}
              />
            </div>
          </div>
        }
      />
    );
  },
);

SessionConfirmationModal.displayName = 'SessionConfirmationModal';

export default SessionConfirmationModal;
